"use client";

import { c as _c } from "react/compiler-runtime";
import { ChartLineUpIcon, CpuIcon, FactoryIcon, GearIcon, HardHatIcon, PackageIcon } from "@phosphor-icons/react";
import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { AnimatedBeam } from "./ui/animated-beam";
import ArpanoLogo from "./ui/arpano-logo";
function Circle(t0) {
  const $ = _c(6);
  const {
    className,
    children,
    ref
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("z-10 flex size-12 items-center justify-center rounded-full border border-primary bg-background p-2 shadow-md dark:shadow-foreground/5", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== ref || $[4] !== t1) {
    t2 = <div ref={ref} className={t1}>{children}</div>;
    $[2] = children;
    $[3] = ref;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
}
export default function ManufacturingBeam() {
  const $ = _c(11);
  const [isDark, setIsDark] = useState(false);
  const containerRef = useRef(null);
  const aiCoreRef = useRef(null);
  const sensorsRef = useRef(null);
  const roboticsRef = useRef(null);
  const cpuRef = useRef(null);
  const analyticsRef = useRef(null);
  const productionRef = useRef(null);
  const outputRef = useRef(null);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      const updateTheme = () => {
        setIsDark(document.documentElement.classList.contains("dark"));
      };
      updateTheme();
      const observer = new MutationObserver(updateTheme);
      observer.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ["class"]
      });
      return () => observer.disconnect();
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  const pathColor = isDark ? "oklch(0.25 0.01 95)" : "oklch(0.75 0.01 95)";
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Circle ref={sensorsRef}><GearIcon className="size-6 text-foreground" weight="duotone" /></Circle>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="flex flex-row items-center justify-between">{t2}<Circle ref={roboticsRef}><HardHatIcon className="size-6 text-foreground" weight="duotone" /></Circle></div>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="flex flex-row items-center justify-center"><Circle ref={aiCoreRef} className="size-16 border-primary"><ArpanoLogo className="size-10 text-primary" /></Circle></div>;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Circle ref={analyticsRef}><ChartLineUpIcon className="size-6 text-foreground" weight="duotone" /></Circle>;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div className="flex size-full flex-col items-stretch justify-between gap-10">{t3}{t4}<div className="flex flex-row items-center justify-between">{t5}<Circle ref={productionRef}><FactoryIcon className="size-6 text-foreground" weight="duotone" /></Circle></div></div>;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <div className="absolute left-4 top-1/2 -translate-y-1/2 flex flex-col gap-4 z-10"><Circle ref={cpuRef}><CpuIcon className="size-6 text-foreground" weight="duotone" /></Circle></div>;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <Circle ref={outputRef} className="absolute right-4 top-1/2 -translate-y-1/2 size-10"><PackageIcon className="size-5 text-foreground" weight="duotone" /></Circle>;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  let t9;
  if ($[9] !== pathColor) {
    t9 = <div ref={containerRef} className="relative flex h-[280px] sm:h-[320px] lg:h-[350px] w-full max-w-lg items-center justify-center overflow-hidden backdrop-blur-sm p-6 sm:p-8 lg:p-10 shadow-xl">{t6}{t7}{t8}<AnimatedBeam containerRef={containerRef} fromRef={cpuRef} toRef={aiCoreRef} curvature={0} pathColor={pathColor} gradientStartColor="oklch(0.6585 0.23 35)" gradientStopColor="oklch(0.75 0.18 45)" /><AnimatedBeam containerRef={containerRef} fromRef={sensorsRef} toRef={aiCoreRef} curvature={-40} pathColor={pathColor} gradientStartColor="oklch(0.6585 0.23 35)" gradientStopColor="oklch(0.75 0.18 45)" /><AnimatedBeam containerRef={containerRef} fromRef={roboticsRef} toRef={aiCoreRef} curvature={40} pathColor={pathColor} gradientStartColor="oklch(0.6585 0.23 35)" gradientStopColor="oklch(0.75 0.18 45)" /><AnimatedBeam containerRef={containerRef} fromRef={aiCoreRef} toRef={analyticsRef} curvature={-80} pathColor={pathColor} gradientStartColor="oklch(0.75 0.18 45)" gradientStopColor="oklch(0.6585 0.23 35)" reverse={true} /><AnimatedBeam containerRef={containerRef} fromRef={aiCoreRef} toRef={productionRef} curvature={-40} pathColor={pathColor} gradientStartColor="oklch(0.75 0.18 45)" gradientStopColor="oklch(0.6585 0.23 35)" reverse={true} /><AnimatedBeam containerRef={containerRef} fromRef={productionRef} toRef={outputRef} curvature={-20} pathColor={pathColor} gradientStartColor="oklch(0.6585 0.23 35)" gradientStopColor="oklch(0.75 0.18 45)" /></div>;
    $[9] = pathColor;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  return t9;
}